import { useMemo } from 'react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { Drn } from '@duke-hq/drn';

type UserData = { name: string; email?: string; type: string };

export function useChatActions(chatUsers: Record<Drn, UserData>, ticketId?: string) {
  const utils = trpcTenant.useUtils();
  const sendTyping = trpcTenant.support.sendTyping.useMutation();
  const sendMessage = trpcTenant.support.sendMessage.useMutation({
    onSuccess: () => {
      if (ticketId !== undefined) {
        void utils.support.getTicket.invalidate({ ticketId });
        void utils.support.listTickets.invalidate();
        void utils.support.getUnreadTicketCount.invalidate();
        void utils.notifications.unreadTicketCount.invalidate();
      }
    },
  });

  return useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/require-await
      sendTyping: async ({ chatId, typing }) => {
        sendTyping.mutate({ chatId, typing });
      },

      // eslint-disable-next-line @typescript-eslint/require-await
      sendMessage: async ({ chatId, message }) => {
        sendMessage.mutate({ chatId, message });
      },

      getUser: (userDrn: Drn) => {
        const userData = chatUsers[userDrn];

        if (userData !== undefined) {
          return {
            drn: userDrn,
            displayName: userData.name,
            lastSeen: undefined, // doesn't seem used, but required in type
          };
        }

        return undefined;
      },

      // eslint-disable-next-line @typescript-eslint/require-await
      refetchChatMeta: async (chatId: string) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        utils.support.chat.getMeta.refetch({ id: chatId });
      },
    }),
    [sendTyping, sendMessage, utils, chatUsers],
  );
}
